import React, { useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import ContactModal from "../components/ContactModal";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { serviceJsonLd, filArianeJsonLd, faqJsonLd } from "../utils/donneesStructurees";
import { listeDepartements } from "../data/departements";
import { trackSimulatorClick } from "../utils/analytics";
import { ArrowRight, CheckCircle, Phone, MessageCircle, TrendingUp, ShieldCheck, Clock } from "lucide-react";
import BoutonRappel from "../components/BoutonRappel";
import GuidesLies from "../components/GuidesLies";
import LienAidesComplementaires from "../components/LienAidesComplementaires";

// Les questions ci-dessous sont affichées ET déclarées en FAQPage : garder
// les deux synchronisées, Google exige que le contenu balisé soit visible.
const questions = [
  {
    id: "prix",
    question: "Combien coûte un monte-escalier et que reste-t-il à payer ?",
    answer:
      "Le prix dépend surtout de la forme de l'escalier : comptez de l'ordre de 3 500 à 6 500 € pour une volée droite, et davantage pour un escalier tournant qui impose un rail sur mesure. Avec MaPrimeAdapt', 70 % du montant est pris en charge pour les ménages aux ressources très modestes et 50 % pour les ménages modestes. Sur un devis de 6 200 € à 50 %, le reste à charge s'établit à 3 100 € avant les aides de votre caisse de retraite et de votre commune.",
  },
  {
    id: "eligible",
    question: "Le monte-escalier est-il bien éligible à MaPrimeAdapt' ?",
    answer:
      "Oui. L'installation d'un monte-escalier fait partie des travaux d'adaptation du logement finançables, dès lors qu'elle répond à une perte d'autonomie constatée lors de la visite d'évaluation à domicile. Le diagnostic réalisé dans le cadre de l'accompagnement AMO justifie ce besoin auprès de l'ANAH.",
  },
  {
    id: "tournant",
    question: "Mon escalier est tournant, est-ce possible ?",
    answer:
      "Oui, mais le rail doit être fabriqué sur mesure, ce qui allonge le délai de fabrication et augmente le prix. Nous faisons chiffrer les deux configurations lorsqu'un doute existe, afin que vous compariez le coût réel après aides.",
  },
  {
    id: "delai",
    question: "Quel est le délai d'installation ?",
    answer:
      "La pose elle-même prend généralement une demi-journée pour un escalier droit. Le délai réel dépend du dossier : environ un mois pour le monter, deux à trois mois d'instruction par l'ANAH, puis la fabrication du rail. Attention, les travaux ne doivent pas démarrer avant l'accord de l'ANAH, sous peine de perdre l'aide.",
  },
  {
    id: "location",
    question: "Vaut-il mieux acheter ou louer un monte-escalier ?",
    answer:
      "MaPrimeAdapt' finance l'achat et l'installation, pas la location. La location peut se justifier pour un besoin temporaire, par exemple après une opération, mais elle ne donne pas droit à l'aide.",
  },
  {
    id: "copropriete",
    question: "Puis-je installer un monte-escalier dans une copropriété ?",
    answer:
      "À l'intérieur de votre logement, vous n'avez besoin d'aucune autorisation. Dans les parties communes, l'accord de la copropriété est nécessaire : la loi facilite ces travaux d'accessibilité, mais la procédure doit être respectée. Nous vous indiquons les démarches applicables à votre situation.",
  },
];

const MonteEscalier = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const points = [
    {
      icon: <TrendingUp className="h-8 w-8 text-orange-700" />,
      titre: "Retrouver son étage",
      texte:
        "Chambre et salle de bain à l'étage redeviennent accessibles, sans avoir à réorganiser tout le rez-de-chaussée ni à déménager.",
    },
    {
      icon: <ShieldCheck className="h-8 w-8 text-green-700" />,
      titre: "Supprimer le risque de chute",
      texte:
        "L'escalier est le lieu des chutes les plus graves à domicile. Le monte-escalier supprime le franchissement des marches.",
    },
    {
      icon: <Clock className="h-8 w-8 text-blue-600" />,
      titre: "Une pose rapide",
      texte:
        "Sur un escalier droit, l'installation se fait en une demi-journée, sans travaux de maçonnerie ni réfection.",
    },
  ];

  const departements = listeDepartements();

  // Fourchettes de marché, données pour situer le reste à charge. Nous ne
  // vendons pas les appareils : le devis vient de l'installateur.
  const modeles = [
    {
      nom: "Monte-escalier droit",
      prix: "3 000 à 5 000 €",
      texte:
        "Une seule volée, sans palier ni virage. Le rail est un produit de série recoupé à la longueur, la pose se fait en une demi-journée et il se fixe sur les marches, jamais sur le mur — le plâtre d'un pavillon ancien ne supporterait pas la charge.",
    },
    {
      nom: "Monte-escalier tournant",
      prix: "8 000 à 12 000 €",
      texte:
        "Escalier avec virage, palier intermédiaire ou colimaçon. Le rail est fabriqué d'après un relevé de votre cage d'escalier, ce qui ajoute trois à six semaines de délai. C'est la configuration la plus fréquente dans les maisons de ville franciliennes.",
    },
    {
      nom: "Plateforme élévatrice",
      prix: "9 000 à 15 000 €",
      texte:
        "Pour rester dans son fauteuil roulant plutôt que d'effectuer un transfert. Elle demande une largeur d'escalier plus importante qu'un siège, ce qui la rend impossible dans certaines cages étroites.",
    },
    {
      nom: "Monte-escalier extérieur",
      prix: "5 000 à 9 000 €",
      texte:
        "Perron, accès au jardin, marches de trottoir à franchir. Rail, moteur et housse sont traités contre les intempéries, et l'alimentation électrique doit être protégée. Souvent oublié dans les dossiers alors que les accès sont la première cause de chute déclarée.",
    },
  ];

  const verifications = [
    "La largeur de l'escalier au point le plus étroit : en dessous de 70 cm environ, un siège reste possible mais une plateforme ne l'est plus.",
    "La présence d'une porte en haut ou en bas des marches, qui peut imposer un rail escamotable pour ne pas bloquer le passage.",
    "Une prise de courant à moins de deux mètres du départ du rail, faute de quoi il faut prévoir une alimentation, donc un électricien au devis.",
    "L'état des marches : un nez de marche descellé ou un bois fatigué se reprend avant la pose, pas après.",
    "Qui utilisera l'appareil, et s'il pourra s'y asseoir seul. C'est ce qui départage un siège d'une plateforme, bien avant le budget.",
  ];

  const etapes = [
    "Visite d'évaluation à domicile et mesure de l'escalier",
    "Devis d'artisans et montage du dossier MaPrimeAdapt'",
    "Dépôt auprès de l'ANAH et attente de l'accord",
    "Fabrication du rail puis installation",
    "Contrôle de la conformité et versement des aides",
  ];

  return (
    <div className="min-h-screen bg-white">
      <Seo
        titre="Monte-escalier : aide ANAH et MaPrimeAdapt' 2026"
        description="Aide ANAH pour un monte-escalier via MaPrimeAdapt' : prix, éligibilité, reste à charge et démarches. AMO agréé ANAH en Île-de-France."
        chemin="/monte-escalier"
        donnees={[
          serviceJsonLd({
            nom: "Installation de monte-escalier financée par MaPrimeAdapt'",
            description:
              "Accompagnement complet pour l'installation d'un monte-escalier : évaluation à domicile, devis, dossier ANAH et suivi jusqu'au versement des aides.",
            url: "/monte-escalier",
          }),
          filArianeJsonLd([
            { nom: "Accueil", url: "/" },
            { nom: "Accessibilité du logement", url: "/accessibilite" },
            { nom: "Monte-escalier", url: "/monte-escalier" },
          ]),
          faqJsonLd(questions),
        ]}
      />
      <Header />

      <main id="contenu">

      {/* Hero */}
      <section className="bg-gradient-to-br from-orange-50 via-white to-orange-50 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 bg-orange-100 text-orange-800 rounded-full text-sm font-medium mb-6">
              <TrendingUp className="h-4 w-4 mr-2" aria-hidden="true" />
              Monte-escalier • MaPrimeAdapt'
            </div>

            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Monte-escalier : aide ANAH, prix et démarches{" "}
              <span className="text-orange-700 block mt-2">avec MaPrimeAdapt' 2026</span>
            </h1>

            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              L'escalier est souvent le premier obstacle qui pousse à quitter son logement.
              Un monte-escalier le supprime, et MaPrimeAdapt' en finance{" "}
              <strong className="text-orange-700">jusqu'à 70 %</strong>. Nous montons votre
              dossier auprès de l'ANAH, de la visite d'évaluation au versement des aides.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/simulateur" onClick={() => trackSimulatorClick("monte_escalier_hero")}>
                <Button size="lg" className="bg-orange-700 hover:bg-orange-800 text-white w-full sm:w-auto group">
                  Tester mon éligibilité
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <BoutonRappel className="border-2 w-full sm:w-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* Pourquoi */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Pourquoi installer un monte-escalier
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {points.map((p) => (
              <div key={p.titre} className="bg-gray-50 rounded-xl p-8 border border-gray-200">
                <div className="mb-4">{p.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{p.titre}</h3>
                <p className="text-gray-700 leading-relaxed">{p.texte}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Types d'appareils — la question posée en premier au téléphone, et
          celle qui explique l'écart de prix du simple au triple. */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Quel appareil pour quel escalier
              </h2>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                C&apos;est la forme de l&apos;escalier qui détermine le prix, bien plus que
                la marque ou les options. Un rail droit est un produit standard ; un rail
                courbe est fabriqué sur mesure d&apos;après un relevé de votre cage
                d&apos;escalier, et ce sur-mesure explique à lui seul un écart qui va
                souvent du simple au double.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {modeles.map((m) => (
                <div key={m.nom} className="bg-white rounded-xl border-2 border-gray-200 p-6">
                  <div className="flex items-baseline justify-between gap-4 mb-3">
                    <h3 className="text-xl font-bold text-gray-900">{m.nom}</h3>
                    <span className="text-orange-700 font-bold whitespace-nowrap">{m.prix}</span>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{m.texte}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 bg-white rounded-xl border-2 border-orange-200 p-6 sm:p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Ce qu&apos;il faut regarder chez vous avant de se lancer
              </h3>
              <ul className="space-y-3">
                {verifications.map((v) => (
                  <li key={v} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-orange-700 flex-shrink-0 mt-1" aria-hidden="true" />
                    <span className="text-gray-700 leading-relaxed">{v}</span>
                  </li>
                ))}
              </ul>
              <p className="text-gray-700 mt-5 leading-relaxed">
                Aucun de ces points n&apos;est rédhibitoire à lui seul, et ils se vérifient
                en quelques minutes pendant la visite d&apos;évaluation à domicile. Les
                relever avant permet surtout d&apos;éviter un devis qu&apos;il faudra
                refaire, et donc de perdre des semaines sur un calendrier déjà long.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Maillage départemental : les aides locales changent le reste à charge,
          et les requêtes du type « monte-escalier 93 » passent par là. */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
              Les aides locales qui s&apos;ajoutent, département par département
            </h2>
            <p className="text-lg text-gray-700 mb-8">
              MaPrimeAdapt&apos; se cumule avec les dispositifs de votre département, qui
              ne sont pas les mêmes d&apos;un point à l&apos;autre de l&apos;Île-de-France.
              À reste à charge identique sur le devis, la facture finale peut varier de
              plusieurs centaines d&apos;euros selon l&apos;endroit où vous habitez.
            </p>
            <ul className="flex flex-wrap justify-center gap-3">
              {departements.map((d) => (
                <li key={d.code}>
                  <Link
                    to={`/amo-anah-${d.cle}-${d.code}`}
                    className="inline-block px-4 py-3 bg-orange-50 border-2 border-orange-100 rounded-lg text-gray-900 font-medium hover:border-orange-700 hover:bg-orange-100 transition-colors"
                  >
                    Monte-escalier {d.ou} ({d.code})
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Combien ça coûte */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 text-center">
              Ce que coûte un monte-escalier après aides
            </h2>
            <p className="text-lg text-gray-700 text-center mb-12">
              Exemple d'un escalier droit, pour un ménage aux ressources modestes.
            </p>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
              <div className="space-y-3">
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="text-gray-700">Fourniture et pose</span>
                  <span className="font-semibold text-gray-900 whitespace-nowrap">6 200 €</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="text-green-700">MaPrimeAdapt' (50 %)</span>
                  <span className="font-semibold text-green-700 whitespace-nowrap">- 3 100 €</span>
                </div>
                <div className="flex justify-between items-center pt-4">
                  <span className="text-lg font-semibold text-gray-900">Reste à charge</span>
                  <span className="text-2xl font-bold text-gray-400 whitespace-nowrap line-through decoration-2 decoration-orange-400">3 100 €</span>
                </div>
              </div>
                <LienAidesComplementaires />

              <p className="text-sm text-gray-700 mt-6">
                Montants indicatifs, avant cumul avec les aides de votre caisse de retraite et de
                votre commune. Pour un ménage aux ressources très modestes, le taux passe à 70 %.
                Les travaux retenus sont plafonnés à 22 000 € HT, soit une aide maximale de 15 400 €.
              </p>
            </div>

            <div className="mt-8 text-center">
              <Link to="/cumul-aides-ma-prime-adapt-idf" className="text-orange-700 underline hover:text-orange-800">
                Voir quelles aides se cumulent en Île-de-France
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Étapes */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-12 text-center">
              Comment se déroule votre projet
            </h2>

            <ol className="space-y-6">
              {etapes.map((etape, i) => (
                <li key={etape} className="flex items-start space-x-4">
                  <span className="flex-shrink-0 w-10 h-10 bg-orange-700 text-white rounded-full flex items-center justify-center font-bold">
                    {i + 1}
                  </span>
                  <span className="text-lg text-gray-700 pt-1">{etape}</span>
                </li>
              ))}
            </ol>

            <div className="mt-12 bg-orange-50 border border-orange-200 rounded-lg p-6">
              <p className="text-gray-800">
                <CheckCircle className="inline h-5 w-5 text-orange-700 mr-2" aria-hidden="true" />
                L'accompagnement AMO est obligatoire pour obtenir MaPrimeAdapt', et son coût est
                pris en charge par l'ANAH : il ne vous reste rien à payer pour cette prestation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-12 text-center">
              Monte-escalier : vos questions
            </h2>

            <Accordion type="single" collapsible className="space-y-4">
              {questions.map((item) => (
                <AccordionItem
                  key={item.id}
                  value={item.id}
                  className="border border-gray-300 rounded-lg px-6 bg-white hover:bg-gray-50 transition-colors"
                >
                  <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-orange-700 py-6">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700 leading-relaxed pb-6">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-orange-600 to-orange-700 py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Savoir en quelques minutes ce que vous paieriez
          </h2>
          <p className="text-xl text-white mb-10 max-w-2xl mx-auto">
            Le simulateur vous donne votre taux de prise en charge. Nous nous occupons du reste.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-lg mx-auto">
            <Link to="/simulateur" onClick={() => trackSimulatorClick("monte_escalier_cta")} className="w-full sm:w-auto">
              <Button size="lg" className="bg-white text-orange-700 hover:bg-gray-100 w-full sm:w-auto shadow-lg">
                Tester mon éligibilité
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-orange-700 w-full sm:w-auto"
              onClick={() => {
                window.location.href = "tel:0939035444";
              }}
            >
              <Phone className="mr-2 h-5 w-5" />
              09 39 03 54 44
            </Button>
          </div>
        </div>
      </section>

      <GuidesLies slugs={["reste-a-charge-adaptation-logement", "comment-obtenir-maprimeadapt"]} />

      </main>

      <Footer />

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        type="general"
      />
    </div>
  );
};

export default MonteEscalier;
